import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import UploadIconSVG from "../../Assets/svg/UploadSVG.svg";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import { CreateBulkAccountAction } from "../../Actions/Authentication";

class Upload extends Component {
  state = {
    file: null,
    isDragging: false,
    error: null,
    isUploading: false,
    uploadResult: null,
    showUploadForm: true,
  };

  static propTypes = {
    navigate: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired,
    CreateBulkAccountAction: PropTypes.func.isRequired,
  };

  componentDidMount() {
    if (!this.props.navigate) {
      console.error("Navigation prop is not available");
    }
  }

  fileInputRef = React.createRef();

  handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const file = e.dataTransfer.files[0];
    if (file) {
      this.validateAndSetFile(file);
    }
  };

  handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.validateAndSetFile(file);
    }
  };

  validateAndSetFile = async (file) => {
    if (!file.name.endsWith(".csv") && !file.name.endsWith(".xlsx")) {
      this.setState({
        error: "Please upload a CSV or Excel file only",
        file: null,
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      this.setState({ error: "File size should not exceed 5MB", file: null });
      return;
    }

    this.setState({ file, error: null, isUploading: true, uploadResult: null });

    try {
      const result = await this.props.CreateBulkAccountAction(file);
      this.setState({
        uploadResult: result,
        isUploading: false,
        showUploadForm: false,
      });
    } catch (error) {
      this.setState({
        error: error.response?.data?.message || "Failed to upload file",
        isUploading: false,
        uploadResult: null,
      });
    }
  };

  handleChooseFile = () => {
    this.fileInputRef.current.click();
  };

  handleBack = () => {
    this.setState({
      file: null,
      isDragging: false,
      error: null,
      isUploading: false,
      uploadResult: null,
      showUploadForm: true,
    });
  };

  renderUploadForm = () => {
    const { isDragging, error, file, isUploading } = this.state;

    return (
      <>
        <div className="bg-white rounded-lg">
          <h2 className="font-SFPro font-[700] text-[2.25rem] mb-2">
            Bulk Upload Users
          </h2>
          <p className="font-inter text-[1.5rem] font-[400] text-[#667085] mb-6">
            Upload a file to create multiple users and department at once
          </p>

          <div
            className={`border-2 border-dashed rounded-lg ${
              isDragging
                ? "border-primary-blue bg-[#E7F1FF]"
                : "border-[#0D6EFD] bg-[#CADFFF]"
            } transition-colors duration-200`}
            onDragEnter={this.handleDragEnter}
            onDragOver={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
          >
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <img
                src={UploadIconSVG}
                alt="Upload"
                className="w-12 h-12 md:w-16 md:h-16 mb-4"
              />
              <p className="font-inter text-base md:text-lg text-center mb-2">
                {file ? file.name : "Drag & Drop your file here"}
              </p>
              <div className="flex items-center gap-2 font-inter text-sm md:text-base">
                <span className="text-[#667085]">or</span>
                <button
                  onClick={this.handleChooseFile}
                  className="text-primary-blue hover:text-primary-blue-dark"
                >
                  choose file
                </button>
              </div>
              <input
                type="file"
                ref={this.fileInputRef}
                onChange={this.handleFileSelect}
                accept=".csv,.xlsx"
                className="hidden"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 text-[#EC474A] text-sm font-inter">
              {error}
            </div>
          )}

          {isUploading && (
            <div className="mt-4 text-primary-blue text-sm font-inter">
              Uploading file...
            </div>
          )}
        </div>
      </>
    );
  };

  renderResultScreen = () => {
    const { uploadResult } = this.state;

    if (!uploadResult) {
      return (
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="font-SFPro font-[700] text-[2.25rem]">
              Upload Results
            </h2>
            <button
              onClick={this.handleBack}
              className="px-4 py-2 text-primary-blue border border-primary-blue rounded-lg hover:bg-primary-blue hover:text-white transition-colors"
            >
              Upload Another File
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#F3F4F6] p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Total Processed</h3>
              <p className="text-3xl font-bold">0</p>
            </div>
            <div className="bg-[#DCFCE7] p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">
                Successfully Created
              </h3>
              <p className="text-3xl font-bold text-[#10B981]">0</p>
            </div>
            <div className="bg-[#F3F4F6] p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Errors</h3>
              <p className="text-3xl font-bold">0</p>
            </div>
          </div>

          <div className="mt-4">
            <h3 className="font-SFPro font-[600] text-xl mb-4">
              Process Summary
            </h3>
            <div className="bg-white rounded-lg p-4 text-[#4B5563]">
              <p className="text-lg text-[#10B981]">
                Processing upload request...
              </p>
            </div>
          </div>
        </div>
      );
    }

    const hasErrors = uploadResult.error_count > 0;

    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h2 className="font-SFPro font-[700] text-[2.25rem]">
            Upload Results
          </h2>
          <button
            onClick={this.handleBack}
            className="px-4 py-2 text-primary-blue border border-primary-blue rounded-lg hover:bg-primary-blue hover:text-white transition-colors"
          >
            Upload Another File
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-[#F3F4F6] p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Total Processed</h3>
            <p className="text-3xl font-bold">
              {uploadResult.total_processed || 0}
            </p>
          </div>
          <div className="bg-[#DCFCE7] p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Successfully Created</h3>
            <p className="text-3xl font-bold text-[#10B981]">
              {uploadResult.success_count || 0}
            </p>
          </div>
          <div
            className={`p-6 rounded-lg ${
              hasErrors ? "bg-[#FEE2E2]" : "bg-[#F3F4F6]"
            }`}
          >
            <h3 className="text-lg font-semibold mb-2">Errors</h3>
            <p
              className={`text-3xl font-bold ${
                hasErrors ? "text-[#EF4444]" : ""
              }`}
            >
              {uploadResult.error_count || 0}
            </p>
          </div>
        </div>

        <div className="mt-4">
          <h3 className="font-SFPro font-[600] text-xl mb-4">
            Process Summary
          </h3>
          <div className="bg-white rounded-lg p-4 text-[#4B5563]">
            <p
              className={`text-lg ${
                hasErrors ? "text-[#EF4444]" : "text-[#10B981]"
              }`}
            >
              {uploadResult.message || "Process completed"}
            </p>
          </div>
        </div>

        {uploadResult.errors && uploadResult.errors.length > 0 && (
          <div className="mt-4">
            <h3 className="font-SFPro font-[600] text-xl mb-4">
              Error Details
            </h3>
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Row
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Error Message
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {uploadResult.errors.map((error, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {error.row}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {error.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-[#EF4444]">
                          {Object.entries(error.errors || {}).map(
                            ([key, messages]) => (
                              <div key={key}>
                                {Array.isArray(messages)
                                  ? messages.join(", ")
                                  : messages}
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { showUploadForm, uploadResult } = this.state;

    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#F8F8F9]">
        <div className="w-full max-w-[1200px] h-full max-h-[700px] flex flex-col bg-white rounded-[1rem] shadow-[0px_0px_10px_rgba(0,0,0,0.1)] p-[2rem] md:p-[3rem] overflow-y-auto">
          <div className="flex items-center gap-4 mb-8">
            <button
              onClick={() => this.props.navigate("/admin-dashboard")}
              className="text-primary-blue hover:text-primary-blue-dark"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="font-SFPro font-[700] text-[2.5rem] md:text-[2rem]">
              File Upload Dashboard
            </h1>
          </div>
          <div className="text-[1.5rem] font-SFPro font-[400] text-primary-blue border-b-2 border-primary-blue w-fit mb-8">
            Upload
          </div>

          {showUploadForm ? this.renderUploadForm() : this.renderResultScreen()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});

export default withRouterHooks(
  connect(mapStateToProps, { CreateBulkAccountAction })(Upload)
);
